import { useEffect, useRef, useState } from "react"
import classnames from "classnames"

import styles from "./carousel.module.scss"

export interface Slide {
  title: string
  body: string
  image: { src: string; width: number; height: number }
}

interface Props {
  slides: Slide[]
}

export const AUTO_ADVANCE_MS = 5000

const Carousel = ({ slides }: Props) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const scrollTargetRef = useRef<number | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  const showSlide = (index: number) => {
    const track = trackRef.current
    const alreadyThere = !!track?.clientWidth && Math.round(track.scrollLeft / track.clientWidth) === index
    scrollTargetRef.current = alreadyThere ? null : index
    setActiveIndex(index)
    track?.scrollTo?.({ left: index * track.clientWidth, behavior: "smooth" })
  }

  const onScroll = () => {
    const track = trackRef.current
    if (!track?.clientWidth) return

    const index = Math.round(track.scrollLeft / track.clientWidth)
    if (scrollTargetRef.current !== null) {
      if (index !== scrollTargetRef.current) return
      scrollTargetRef.current = null
    }
    setActiveIndex(index)
  }

  const releaseScrollTarget = () => {
    scrollTargetRef.current = null
  }

  const step = (delta: number) => showSlide((activeIndex + delta + slides.length) % slides.length)

  useEffect(() => {
    if (paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    const timer = window.setTimeout(() => step(1), AUTO_ADVANCE_MS)
    return () => window.clearTimeout(timer)
  })

  return (
    <section
      className={styles["carousel"]}
      aria-roledescription="carousel"
      aria-label="IFTTT Pro features"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <button
        type="button"
        className={classnames(styles["edge"], styles["edge--previous"])}
        aria-label="Previous slide"
        tabIndex={-1}
        onClick={() => step(-1)}
      />
      <button
        type="button"
        className={classnames(styles["edge"], styles["edge--next"])}
        aria-label="Next slide"
        tabIndex={-1}
        onClick={() => step(1)}
      />
      <div
        className={styles["track"]}
        ref={trackRef}
        onScroll={onScroll}
        onPointerDown={releaseScrollTarget}
        onWheel={releaseScrollTarget}
        onKeyDown={releaseScrollTarget}
      >
        {slides.map((slide, index) => (
          <div
            key={slide.title}
            className={styles["slide"]}
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${slides.length}`}
            aria-hidden={index !== activeIndex}
          >
            <img
              className={styles["image"]}
              src={slide.image.src}
              width={slide.image.width}
              height={slide.image.height}
              alt=""
            />
            <div className={styles["text"]}>
              <h2 className={styles["title"]}>{slide.title}</h2>
              <p className={styles["body"]}>{slide.body}</p>
            </div>
          </div>
        ))}
      </div>

      <div className={styles["dots"]}>
        {slides.map((slide, index) => (
          <button
            key={slide.title}
            type="button"
            className={classnames(styles["dot"], { [styles["dot--active"]]: index === activeIndex })}
            aria-label={`Show slide ${index + 1} of ${slides.length}`}
            aria-current={index === activeIndex}
            onClick={() => showSlide(index)}
          />
        ))}
      </div>
    </section>
  )
}

export default Carousel
